import {
  Box,
  Card,
  CardContent,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { FC, useEffect } from "react";

import { Authenticator } from "@aws-amplify/ui-react";
import { useLocation, useNavigate } from "react-router-dom";

import useAuth from "../hooks/auth.hook";
import ThemeModeToggle from "./theme-mode-toggle.component";

const authtenticatorFormFields = {
  signIn: {
    username: {
      placeholder: "Email",
    },
  },
  signUp: {
    given_name: {
      placeholder: "First Name",
      order: 1,
    },
    family_name: {
      placeholder: "Last Name",
      order: 2,
    },
    username: {
      placeholder: "Email",
      order: 3,
    },
  },
  resetPassword: {
    username: {
      placeholder: "Email",
    },
  },
};

const Login: FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const from = (location.state as any)?.from?.pathname || "/";

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  return (
    <Box
      sx={{ minHeight: "100vh", display: "flex", alignItems: "center", py: 4 }}
    >
      <Container maxWidth="sm">
        <Stack spacing={2.5}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <ThemeModeToggle />
          </Box>

          <Card>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Stack spacing={3}>
                <Box>
                  <Typography
                    variant="overline"
                    sx={{ color: "text.secondary", letterSpacing: "0.14em" }}
                  >
                    Welcome back
                  </Typography>
                  <Typography variant="h4" gutterBottom>
                    MTG Deck Builder
                  </Typography>
                  <Typography color="text.secondary">
                    Turn Bulk Into Battle-Ready Decks
                  </Typography>
                </Box>
                <Box
                  sx={{
                    "& [data-amplify-authenticator]": {
                      display: "block",
                    },
                    "& [data-amplify-container]": {
                      width: "100%",
                    },
                    "& .amplify-authenticator__heading": {
                      fontFamily: theme.typography.fontFamily,
                      fontWeight: 600,
                      fontSize: theme.typography.h6.fontSize,
                      lineHeight: theme.typography.h6.lineHeight,
                      marginBottom: theme.spacing(1.5),
                    },
                    "& .amplify-authenticator__subtitle": {
                      color: theme.palette.text.secondary,
                    },
                    "& .amplify-tabs__list": {
                      gap: theme.spacing(1),
                      marginBottom: theme.spacing(0.5),
                    },
                    "& .amplify-tabs__item": {
                      borderTopLeftRadius: `${theme.shape.borderRadius}px`,
                      borderTopRightRadius: `${theme.shape.borderRadius}px`,
                      minHeight: 44,
                      fontFamily: theme.typography.fontFamily,
                    },
                    "& .amplify-tabs__item--active": {
                      color: theme.palette.text.primary,
                    },
                    "& .amplify-field": {
                      gap: theme.spacing(0.75),
                    },
                    "& .amplify-label": {
                      fontWeight: 600,
                      letterSpacing: "0.02em",
                    },
                    "& .amplify-input": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha("#FFFFFF", 0.04)
                          : alpha("#FFFFFF", 0.88),
                    },
                    "& .amplify-input::placeholder": {
                      color: theme.palette.text.secondary,
                      opacity: 1,
                    },
                    "& .amplify-button": {
                      minHeight: 44,
                    },
                    "& .amplify-button--link": {
                      justifyContent: "flex-start",
                      paddingInline: 0,
                    },
                    "& .amplify-alert": {
                      border: `1px solid ${alpha(
                        theme.palette.error.main,
                        theme.palette.mode === "dark" ? 0.34 : 0.2,
                      )}`,
                      borderRadius: `${theme.shape.borderRadius}px`,
                    },
                    "& .amplify-field__error-message": {
                      marginTop: theme.spacing(0.25),
                    },
                  }}
                >
                  <Authenticator
                    formFields={authtenticatorFormFields}
                    signUpAttributes={["given_name", "family_name"]}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
};

export default Login;
